import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import { apiGetRedemptionHistory } from '../../../../core/redux/api/endpoints';
import { IAppState } from '../../../../core/redux/IAppState';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { EntitlementRedemptionHistory } from './EntitlementRedemptionHistory';
import { assoc } from '../../../../language/module';


export const KEY_ACTION = ACTION_TYPE.REDEMPTION_HISTORY_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.REDEMPTION_HISTORY_REQUEST,
  ACTION_TYPE.REDEMPTION_HISTORY_RESPONSE,
  ACTION_TYPE.REDEMPTION_HISTORY_ERROR,
];


/**
 * Makes an action that can be dispatched to get a guest's history at a resort from their passes.
 */
export const getRedemptionHistoryAction = (
  guestId: string,
) => makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: state => apiGetRedemptionHistory(guestId, state.resortToolsConfig.enableRedemptionDisplayOnly, state.selectedResort.resortAbbreviation),
    types:    ACTIONS.map(action => ({
      type: action,
      meta: { guestId },
    })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, guestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<EntitlementRedemptionHistory[]> = (state, action, error, isLoading, payload) => {
  const guestId = action.meta.guestId;

  // If we're loading, remove the DTW Redemption's errors and add a loading state
  if (isLoading) {
    return addLoading(state, guestId!, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      guestId!,
      KEY_ACTION,
      error,
    );
  }

  // Remove loading/error since we're not loading or erroring
  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  // Results are an array though Patrick said this should be a single-element array 95% of the time
  const history = (payload || []).filter((historyElement => historyElement.masterGuestId === guestId));

  if (!history) {
    return noLoadingOrError;
  }

  return assoc(
    'guestRedemptionHistories',
    noLoadingOrError.guestRedemptionHistories.add(guestId, history),
    noLoadingOrError,
  );
};


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to a guest's redemptions at resorts while using a specific pass. Returns a new app state, of course.
 */
export const getRedemptionHistoryReducer = makeApiReducer<EntitlementRedemptionHistory[]>(ACTIONS, KEY_ACTION, reducer);
